import { NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { P } from '../auth/permissions'
import type { ReactNode } from 'react'

interface NavEntry {
  to: string
  label: string
  /** Hidden unless the user holds at least one of these. The server checks again on every call. */
  needs?: string[]
}

interface NavSection {
  label: string
  items: NavEntry[]
}

const SECTIONS: NavSection[] = [
  {
    label: 'Daily',
    items: [
      { to: '/', label: 'Dashboard' },
      { to: '/books', label: 'Cash & safe books', needs: [P.cashBook.view] },
      { to: '/journal', label: 'Journal entries', needs: [P.journal.view] },
      { to: '/reconciliation', label: 'Bank reconciliation', needs: [P.cashBook.view] },
      { to: '/documents', label: 'Document intake', needs: [P.documents.view] },
    ],
  },
  {
    label: 'Master data',
    items: [
      { to: '/accounts', label: 'Chart of accounts', needs: [P.accounts.view] },
      { to: '/cash-locations', label: 'Banks & safes', needs: [P.cashBook.view] },
      { to: '/parties', label: 'Clients & vendors', needs: [P.parties.view] },
      { to: '/departments', label: 'Cost centres', needs: [P.accounts.view] },
      { to: '/exchange-rates', label: 'Currencies & rates', needs: [P.currencies.view] },
      { to: '/import', label: 'Excel import', needs: [P.accounts.import, P.parties.create, P.journal.post] },
    ],
  },
  {
    label: 'Reports',
    items: [
      { to: '/reports/profit-and-loss', label: 'P&L — cash vs accrual', needs: [P.reports.view] },
      { to: '/reports/client-funds', label: 'Client funds control', needs: [P.reports.clientFunds] },
      { to: '/reports/trial-balance', label: 'Trial balance', needs: [P.reports.view] },
      { to: '/reports/balance-sheet', label: 'Balance sheet', needs: [P.reports.view] },
      { to: '/reports/cash-flow', label: 'Cash-flow statement', needs: [P.reports.view] },
      { to: '/reports/ga-analysis', label: 'G&A analysis', needs: [P.reports.view] },
      { to: '/reports/aging', label: 'Aging', needs: [P.reports.view] },
      { to: '/reports/employee-schedule', label: 'Employee custody & loans', needs: [P.reports.view] },
      { to: '/reports/account-statement', label: 'Account statement', needs: [P.reports.view] },
      { to: '/reports/tax-summary', label: 'VAT & withholding tax', needs: [P.reports.view] },
    ],
  },
  {
    label: 'Period',
    items: [{ to: '/periods', label: 'Close & lock', needs: [P.periods.view] }],
  },
  {
    label: 'Administration',
    items: [
      { to: '/admin/users', label: 'Users', needs: [P.admin.users] },
      { to: '/admin/roles', label: 'Roles & permissions', needs: [P.admin.roles] },
      { to: '/admin/settings', label: 'Settings', needs: [P.admin.settings] },
      { to: '/admin/audit', label: 'Audit log', needs: [P.admin.auditLog] },
    ],
  },
]

export function AppShell({ children }: { children: ReactNode }) {
  const { user, signOut, canAny } = useAuth()
  const location = useLocation()

  const visible = SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => !item.needs || canAny(...item.needs)),
  })).filter((section) => section.items.length > 0)

  const title =
    visible.flatMap((s) => s.items).find((item) => item.to === location.pathname)?.label ??
    'ALX Accounting'

  return (
    <div className="shell">
      <nav className="sidebar">
        <div className="sidebar-brand">
          <strong>ALX Accounting</strong>
          <span>Dual-basis · single company</span>
        </div>

        {visible.map((section) => (
          <div className="nav-group" key={section.label}>
            <div className="nav-group-label">{section.label}</div>
            {section.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) => (isActive ? 'nav-item active' : 'nav-item')}
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        ))}

        <div className="sidebar-footer">
          <div style={{ color: '#fff', fontWeight: 600 }}>{user?.fullNameEn}</div>
          <div style={{ color: '#92a8bd', marginBottom: 8 }}>{user?.roles.join(', ')}</div>
          <button className="small" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </nav>

      <div className="main">
        <header className="topbar">
          <h1>{title}</h1>
        </header>
        <div className="content">{children}</div>
      </div>
    </div>
  )
}
